import { Avatar, Body, Tooltip } from '@assembly-js/design-system';
import { useMediaQuery, useTheme } from '@material-ui/core';
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  LoadingIndicator,
  useChannelStateContext,
  useChatContext,
  useMessageContext,
  useTranslationContext,
} from 'stream-chat-react';
import { makeStyles } from 'tss-react/mui';

import { CheckedCircleIcon } from 'src/components/icons';
import { RootState } from 'src/store';
import { DividersAndCardBorders, white } from 'src/theme/colors';
import { addCustomCopyBehavior } from 'src/utils/ClipboardUtils';
import { getInitials, getUserIdFromFullId } from 'src/utils/UserUtils';

const useStyles = makeStyles()((theme) => ({
  flexCenterWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  statusIcon: {
    fontSize: 16,
    fill: white,
  },
  deliveredIcon: {
    color: theme.palette.primary.main,
  },
}));

export type MessageStatusProps = {
  messageType?: string;
};
const DESKTOP_MAX_DISPLAYED_READ_RECIPIENTS = 3;
const MOBILE_MAX_DISPLAYED_READ_RECIPIENTS = 1;
const formatter = new Intl.ListFormat('en', {
  style: 'long',
  type: 'conjunction',
});

export const MessageStatus: React.FC<MessageStatusProps> = React.memo(
  ({ messageType = 'simple' }) => {
    const { clients, internalUsers } = useSelector(
      (state: RootState) => ({
        clients: state.clients.activeClients,
        internalUsers: state.users.internalUsers,
      }),
      shallowEqual,
    );

    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('xs'));
    const { classes } = useStyles();

    /**
     * This function gets get-stream user
     * avatar data from the redux store.
     * @param userRole: user role indicates if its an internal user or client user
     * @param recipientUserId: full user id
     * @returns
     */
    const getUserAvatarData = (userRole: string, recipientUserId: string) => {
      const user =
        userRole === 'admin'
          ? internalUsers.find(
              (internalUser) =>
                internalUser.id === getUserIdFromFullId(recipientUserId),
            )
          : clients.find(
              (clientUser) =>
                clientUser.id === getUserIdFromFullId(recipientUserId),
            );
      if (!user) return {};
      return {
        src: user.fields.avatarImageUrl,
        name: `${user.fields.givenName} ${user.fields.familyName}`,
        fallbackColor: user.fields.fallbackColor,
      };
    };

    const { client } = useChatContext('MessageStatus');
    const { members } = useChannelStateContext('MessageStatus');
    const channelMembersIds = React.useMemo(
      () => Object.keys(members || {}),
      [members],
    );

    const { isMyMessage, lastReceivedId, message, readBy, threadList } =
      useMessageContext('MessageStatus');
    const { t } = useTranslationContext('MessageStatus');
    const maxDisplayedReadRecipients = React.useMemo(
      () =>
        !isMobileScreen
          ? DESKTOP_MAX_DISPLAYED_READ_RECIPIENTS
          : MOBILE_MAX_DISPLAYED_READ_RECIPIENTS,
      [isMobileScreen, message],
    );
    if (!isMyMessage() || message.type === 'error') {
      return null;
    }

    const justReadByMe =
      readBy?.length === 1 && readBy.at(0)?.id === client.user?.id;
    // message receipts should include only messages receivers who read
    // the message except the current user.
    const messageReceipts = React.useMemo(
      () =>
        (readBy &&
          readBy.filter(
            (recipientUser) =>
              recipientUser.id !== client.user?.id &&
              channelMembersIds.includes(recipientUser.id),
          )) ||
        [],
      [client, readBy, channelMembersIds],
    );

    if (message.status === 'sending') {
      return (
        <span
          className={`str-chat__message-${messageType}-status`}
          data-testid="message-status-sending"
          {...addCustomCopyBehavior({ excludeFromCopy: true })}
        >
          <LoadingIndicator />
        </span>
      );
    }

    if (readBy?.length && !threadList && !justReadByMe) {
      const lastReadUsers = messageReceipts.slice(
        0,
        maxDisplayedReadRecipients,
      );

      return (
        <span
          className={`str-chat__message-${messageType}-status`}
          data-testid="message-status-read-by"
          {...addCustomCopyBehavior({ excludeFromCopy: true })}
        >
          {/* Other recipients count */}
          {messageReceipts.length > maxDisplayedReadRecipients && (
            <Avatar
              size="3xs"
              variant="circle"
              className="bg-gray-100 border-none mr-[2px]"
              text={`+${messageReceipts.length - maxDisplayedReadRecipients}`}
              fallbackColor={DividersAndCardBorders}
            />
          )}

          {/* Recipients avatars */}
          <Tooltip
            position="left"
            content={
              <Body size="sm">
                Read by&nbsp;
                <span className="font-bold">
                  {formatter.format(
                    messageReceipts.map((user) => user.name).filter(Boolean),
                  )}
                </span>
              </Body>
            }
          >
            <div className={classes.flexCenterWrapper}>
              {lastReadUsers.map((user) => (
                <Avatar
                  size="3xs"
                  key={user.id}
                  variant="circle"
                  className="mr-[2px]"
                  text={getInitials(user.name ?? '', { twoLetters: true })}
                  {...getUserAvatarData(user.role || '', user.id)}
                />
              ))}
            </div>
          </Tooltip>
        </span>
      );
    }

    if (
      message.status === 'received' &&
      message.id === lastReceivedId &&
      !threadList
    ) {
      return (
        <span
          className={`str-chat__message-${messageType}-status`}
          data-testid="message-status-received"
          {...addCustomCopyBehavior({ excludeFromCopy: true })}
        >
          <Tooltip
            position="left"
            content={<Body size="sm">{t('Delivered')}</Body>}
          >
            <div className={classes.flexCenterWrapper}>
              <CheckedCircleIcon
                className={`${classes.statusIcon} ${classes.deliveredIcon}`}
              />
            </div>
          </Tooltip>
        </span>
      );
    }

    return null;
  },
);
